import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../Utils/Axios";

function Blog() {
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/admin/blogs");
      setPosts(response.data.blogs || []);
    } catch (error) {
      console.error("Fetch blogs error:", error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fallback dummy posts if API fails
  const dummyPosts = [
    {
      id: 1,
      title: "Innovative Solutions for every Business Success.",
      excerpt: "Discover how innovative solutions can transform your business and drive sustainable growth in today's competitive market.",
      date: "28 Feb",
      author: "Ellinien Loma",
      category: "Business",
      image: "📊"
    },
    {
      id: 2,
      title: "Harnessing Digital Transform a Roadmap Businesses.",
      excerpt: "Learn the essential steps to digital transformation and how it can revolutionize your business operations and customer experience.",
      date: "28 Feb",
      author: "Ellinien Loma",
      category: "Business",
      image: "💻"
    },
    {
      id: 3,
      title: "Mastering Change Management Lessons for Businesses.",
      excerpt: "Explore proven strategies for managing organizational change and ensuring successful implementation of new initiatives.",
      date: "28 Feb",
      author: "Ellinien Loma",
      category: "Business",
      image: "📈"
    },
    {
      id: 4,
      title: "Building Customer Loyalty Through Exceptional Service.",
      excerpt: "Understand the key principles of customer service excellence and how they contribute to long-term business success.",
      date: "25 Feb",
      author: "John Doe",
      category: "Customer Experience",
      image: "💼"
    },
    {
      id: 5,
      title: "Sustainable Business Practices for Modern Companies.",
      excerpt: "Learn how integrating sustainability into your business strategy can drive value and build stakeholder trust.",
      date: "22 Feb",
      author: "Jane Smith",
      category: "Sustainability",
      image: "🌱"
    },
    {
      id: 6,
      title: "The Future of Remote Work and Team Collaboration.",
      excerpt: "Explore the evolving landscape of remote work and discover tools and strategies for effective team collaboration.",
      date: "20 Feb",
      author: "Mike Johnson",
      category: "Technology",
      image: "🌐"
    }
  ];

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet><title>Blog - Bexon Business Solutions</title></Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-gray-900 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
        <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            {companyLanguage === 'ur' ? 'ہمارا بلاگ' : 'Our Blog'}
          </h1>
          <p className="text-xl md:text-2xl text-gray-800 font-medium">
            {companyLanguage === 'ur' ? 'بصیرت اور خیالات - بہترین ذریعہ' : 'Insights & Ideas - The Ultimate Resource'}
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className={`py-20 bg-gradient-to-b from-white to-gray-50 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
              <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'بلاگز لوڈ ہو رہے ہیں...' : 'Loading blogs...'}
              </p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'کوئی بلاگ نہیں ملا' : 'No blogs found'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Link
                  key={post._id}
                  to={`/blog/${post._id}`}
                  className={`bg-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 group border border-gray-100 hover:border-yellow-300 transform hover:-translate-y-2 ${isUrdu ? 'font-urdu' : ''}`}
                  style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                >
                  <div className="h-56 bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center overflow-hidden relative">
                    {post.image ? (
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <div className="w-24 h-24 bg-yellow-400 rounded-full flex items-center justify-center">
                        <span className="text-5xl">📝</span>
                      </div>
                    )}
                    <div className="absolute top-4 right-4">
                      {post.category && (
                        <span className={`bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-semibold shadow-lg ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {isUrdu && post.categoryUrdu ? post.categoryUrdu : post.category}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3 flex-wrap gap-2">
                      {post.publishedDate && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(post.publishedDate).toLocaleDateString()}
                        </span>
                      )}
                      {post.author && (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className={isUrdu ? 'font-urdu' : ''} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {isUrdu && post.authorUrdu ? post.authorUrdu : post.author}
                          </span>
                        </span>
                      )}
                    </div>
                    <h3 className={`text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-600 transition line-clamp-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu && post.titleUrdu ? post.titleUrdu : post.title}
                    </h3>
                    <p className={`text-gray-600 mb-4 line-clamp-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {isUrdu && post.excerptUrdu ? post.excerptUrdu : post.excerpt}
                    </p>
                    <span className={`inline-flex items-center text-yellow-600 font-semibold group-hover:text-yellow-700 transition ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                      {companyLanguage === 'ur' ? 'مزید پڑھیں' : 'Read More'}
                      <svg className={`w-5 h-5 ${isUrdu ? 'mr-2' : 'ml-2'} group-hover:translate-x-1 transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isUrdu ? "M10 19l-7-7m0 0l7-7m-7 7h18" : "M17 8l4 4m0 0l-4 4m4-4H3"} />
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className={`py-20 bg-gradient-to-br from-yellow-50 via-yellow-100 to-yellow-50 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-block mb-4">
              <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <h2 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'ہمارے نیوز لیٹر کو سبسکرائب کریں' : 'Subscribe to Our Newsletter'}
            </h2>
            <p className={`text-gray-700 mb-8 text-lg ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'تازہ ترین بصیرت اور اپ ڈیٹس اپنے ان باکس میں حاصل کریں۔' : 'Get the latest insights and updates delivered to your inbox.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder={companyLanguage === 'ur' ? 'اپنا ای میل درج کریں' : "Enter your email"}
                className="flex-1 px-6 py-4 rounded-xl border-2 border-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white text-gray-900 placeholder-gray-500"
                dir={isUrdu ? 'rtl' : 'ltr'}
              />
              <button className={`bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'سبسکرائب کریں' : 'Subscribe'}
              </button>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}

export default Blog;

